import {
  Button,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { CheckCircle, Eye } from "lucide-react";
import type { MeetingActionListItem } from "@/modules/management/meeting-minutes/domain/types";
import { formatDateLocal } from "@/shared/lib/date-time";
import { MeetingActionStatusBadge } from "./MeetingActionStatusBadge";

type MeetingActionsTableProps = {
  data: MeetingActionListItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onViewMeeting?: (row: MeetingActionListItem) => void;
  onViewDetail?: (row: MeetingActionListItem) => void;
  onMarkDone?: (row: MeetingActionListItem) => void;
};

export function MeetingActionsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onViewMeeting,
  onViewDetail,
  onMarkDone,
}: MeetingActionsTableProps) {
  const columns: TanstackReactTable.ColumnDef<MeetingActionListItem>[] = [
    createSortableColumn<MeetingActionListItem>("description", "ປະເດັນ", {
      size: 240,
      cell: ({ row }) => {
        const raw = row.original.description ?? "";
        const plainText =
          typeof raw === "string" && raw.trim().startsWith("<")
            ? raw.replace(/<[^>]*>/g, "").trim()
            : raw;
        return (
          <div>
            {onViewDetail ? (
              <button
                type="button"
                className="line-clamp-2 text-left font-medium text-primary hover:underline"
                onClick={() => onViewDetail(row.original)}
              >
                {plainText}
              </button>
            ) : (
              <span className="line-clamp-2">{plainText}</span>
            )}
          </div>
        );
      },
    }),
    createSortableColumn<MeetingActionListItem>("meetingTitle", "ຫົວຂໍ້ປະຊຸມ", {
      size: 200,
      cell: ({ row }) => (
        <div>
          {onViewMeeting ? (
            <button
              type="button"
              className="font-medium text-primary hover:underline"
              onClick={() => onViewMeeting(row.original)}
            >
              {row.original.meetingTitle}
            </button>
          ) : (
            row.original.meetingTitle
          )}
        </div>
      ),
    }),
    createSortableColumn<MeetingActionListItem>("meetingDate", "ວັນທີປະຊຸມ", {
      size: 110,
      cell: ({ row }) => formatDateLocal(row.original.meetingDate),
    }),
    createSortableColumn<MeetingActionListItem>("dueDate", "ວັນສົ່ງ", {
      size: 110,
      cell: ({ row }) =>
        row.original.dueDate ? formatDateLocal(row.original.dueDate) : "—",
    }),
    {
      accessorKey: "assigneeName",
      header: "ຜູ້ຮັບຜິດຊອບ",
      size: 140,
      cell: ({ row }) => row.original.assigneeName ?? "—",
    },
    {
      accessorKey: "status",
      header: "ສະຖານະ",
      size: 100,
      cell: ({ row }) => (
        <MeetingActionStatusBadge status={row.original.status} />
      ),
    },
    {
      id: "actions",
      header: "",
      size: 100,
      cell: ({ row }: { row: { original: MeetingActionListItem } }) => (
        <div className="flex items-center gap-1">
          {onViewDetail && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onViewDetail(row.original)}
              title="ເບິ່ງລາຍລະອຽດ"
            >
              <Eye className="h-4 w-4" />
            </Button>
          )}
          {onMarkDone && row.original.status === "pending" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => onMarkDone(row.original)}
            >
              <CheckCircle className="h-4 w-4" />
              ສຳເລັດ
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={data}
      isLoading={isLoading}
      totalCount={totalCount}
      offset={offset}
      limit={limit}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (!sorting[0] || !sorting[0].id || sorting[0].id === "") return;
        onSortingChange(sorting[0].id as string, !!sorting[0].desc);
      }}
    />
  );
}
